import { useState, useMemo, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { SearchInput } from "@/components/common/SearchInput";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { relative } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ChevronRight, Plus, Pencil, Trash2, FlaskConical, Bug } from "lucide-react";
import { toast } from "sonner";

const RESULT_FAMILY = { pass: "green", fail: "red", blocked: "amber" };

export default function TestCases() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, me } = useApp();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(null);
  const [moduleFilter, setModuleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [notes, setNotes] = useState({});

  const { data: cases = [], isLoading } = useQuery({ queryKey: ["test-cases", id], queryFn: () => get(`/pm/projects/${id}/test-cases`) });
  const { data: allItems = [] } = useQuery({ queryKey: ["items", id], queryFn: () => get(`/pm/projects/${id}/items`) });
  const modules = allItems.filter((i) => i.type === "Module");
  const modMap = Object.fromEntries(modules.map((m) => [m.id, m]));
  const invalidate = () => qc.invalidateQueries({ queryKey: ["test-cases", id] });

  const create = useMutation({ mutationFn: (body) => post(`/pm/projects/${id}/test-cases`, body), onSuccess: () => { invalidate(); setModal(null); } });
  const update = useMutation({ mutationFn: ({ tcid, body }) => patch(`/pm/test-cases/${tcid}`, body), onSuccess: () => { invalidate(); setModal(null); } });
  const remove = useMutation({ mutationFn: (tcid) => del(`/pm/test-cases/${tcid}`), onSuccess: () => { invalidate(); toast.success("Test case deleted"); } });
  const runTest = useMutation({
    mutationFn: ({ tcid, status }) => post(`/pm/test-cases/${tcid}/runs`, { status, run_by: me?.id, notes: notes[tcid] || "" }),
    onSuccess: (_r, { tcid }) => { invalidate(); setNotes((n) => ({ ...n, [tcid]: "" })); },
  });
  const deleteRun = useMutation({ mutationFn: (trid) => del(`/pm/test-runs/${trid}`), onSuccess: invalidate });
  const createBug = useMutation({
    mutationFn: (tcid) => post(`/pm/test-cases/${tcid}/create-bug`, { _actor: me?.id }),
    onSuccess: () => { invalidate(); toast.success("Linked bug created"); },
  });

  const filtered = useMemo(() => {
    return cases.filter((tc) => {
      if (moduleFilter !== "all" && tc.module_id !== moduleFilter) return false;
      if (search && !tc.title?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all") {
        const latest = tc.runs[0];
        if (statusFilter === "not_run") { if (latest) return false; }
        else if (latest?.status !== statusFilter) return false;
      }
      return true;
    });
  }, [cases, moduleFilter, search, statusFilter]);

  if (isLoading) return null;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        {can("pm", "create") && (
          <Button size="sm" variant="outline" data-testid="add-test-case-btn" onClick={() => setModal({ mode: "new" })}>
            <Plus className="mr-1 h-3.5 w-3.5" />Add Test Case
          </Button>
        )}
        <div className="flex items-center gap-2">
          <SearchInput testid="testcase-search" placeholder="Search title" value={search} onChange={setSearch} className="w-56" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="tc-status-filter"><SelectValue placeholder="All results" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All results</SelectItem>
              <SelectItem value="pass">Pass</SelectItem>
              <SelectItem value="fail">Fail</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="not_run">Not run</SelectItem>
            </SelectContent>
          </Select>
          <Select value={moduleFilter} onValueChange={setModuleFilter}>
            <SelectTrigger className="w-48" data-testid="tc-module-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All modules</SelectItem>{modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {cases.length === 0 ? <EmptyState icon={FlaskConical} title="No test cases" /> :
       filtered.length === 0 ? <EmptyState icon={FlaskConical} title="No test cases match your filters" /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="test-cases-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="w-8"></th>
                <th className="px-3 py-2.5 font-semibold">Title</th>
                <th className="px-3 py-2.5 font-semibold">Module</th>
                <th className="px-3 py-2.5 font-semibold">Latest Result</th>
                <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tc) => {
                const latest = tc.runs[0];
                return (
                  <Fragment key={tc.id}>
                    <tr data-testid={`test-case-row-${tc.id}`} className="border-b border-border hover:bg-muted/50">
                      <td className="pl-2"><button onClick={() => setExpanded((e) => ({ ...e, [tc.id]: !e[tc.id] }))}>
                        <ChevronRight className={cn("h-4 w-4 text-muted-foreground transition-transform", expanded[tc.id] && "rotate-90")} />
                      </button></td>
                      <td className="px-3 py-2.5 font-medium">{tc.title}</td>
                      <td className="px-3 py-2.5">{modMap[tc.module_id] ? <StatusBadge family="slate" label={modMap[tc.module_id].title} /> : <span className="text-xs text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2.5">{latest ? <StatusBadge family={RESULT_FAMILY[latest.status]} label={latest.status} /> : <span className="text-xs text-muted-foreground">Not run</span>}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {can("pm", "edit") && (
                            <>
                              <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => runTest.mutate({ tcid: tc.id, status: "pass" })}>Pass</Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => runTest.mutate({ tcid: tc.id, status: "fail" })}>Fail</Button>
                              <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => runTest.mutate({ tcid: tc.id, status: "blocked" })}>Blocked</Button>
                              <Button variant="ghost" size="icon" onClick={() => setModal({ mode: "edit", data: tc })}><Pencil className="h-3.5 w-3.5" /></Button>
                            </>
                          )}
                          {can("pm", "delete") && (
                            <ConfirmDelete testid={`del-test-case-${tc.id}`} title={`Delete "${tc.title}"?`}
                              trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                              onConfirm={() => remove.mutate(tc.id)} />
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded[tc.id] && (
                      <tr>
                        <td colSpan={5} className="bg-muted/30 px-3 py-2">
                          <div className="ml-6 space-y-2 text-sm">
                            <div><span className="text-xs font-medium text-muted-foreground">Steps: </span>{tc.steps || "—"}</div>
                            <div><span className="text-xs font-medium text-muted-foreground">Expected: </span>{tc.expected_result || "—"}</div>
                            {can("pm", "edit") && (
                              <div className="flex items-center gap-2">
                                <Input placeholder="Notes for next run" className="h-7 max-w-xs text-xs" data-testid={`tc-notes-${tc.id}`}
                                  value={notes[tc.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [tc.id]: e.target.value }))} />
                              </div>
                            )}
                            {latest?.status === "fail" && (
                              tc.linked_bug_id ? (
                                <button className="flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => navigate(`/pm/projects/${id}/items/${tc.linked_bug_id}`)}>
                                  <Bug className="h-3.5 w-3.5" />View linked bug
                                </button>
                              ) : can("pm", "create") && (
                                <Button size="sm" variant="outline" data-testid={`create-bug-${tc.id}`} onClick={() => createBug.mutate(tc.id)}>
                                  <Bug className="mr-1.5 h-3.5 w-3.5" />Create Linked Bug
                                </Button>
                              )
                            )}
                            {tc.runs.length > 0 && (
                              <div className="mt-2 space-y-1">
                                <div className="text-xs font-medium text-muted-foreground">Run History</div>
                                {tc.runs.map((r) => (
                                  <div key={r.id} className="flex items-center gap-2 text-xs">
                                    <StatusBadge family={RESULT_FAMILY[r.status]} label={r.status} />
                                    <span className="text-muted-foreground">{relative(r.run_at)}</span>
                                    {r.notes && <span className="text-muted-foreground">· {r.notes}</span>}
                                    {can("pm", "delete") && <button onClick={() => deleteRun.mutate(r.id)} className="ml-auto text-muted-foreground hover:text-red-600"><Trash2 className="h-3 w-3" /></button>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <TestCaseModal state={modal} setState={setModal} modules={modules}
        onSubmit={(body) => modal?.mode === "new" ? create.mutate(body) : update.mutate({ tcid: modal.data.id, body })} />
    </div>
  );
}

function TestCaseModal({ state, setState, onSubmit, modules }) {
  const [form, setForm] = useState({});
  const d = state?.data || {};
  const val = (k) => (form[k] !== undefined ? form[k] : d[k]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm({}); }} key={d.id || "new-tc"}>
      <DialogContent className="max-w-md" data-testid="test-case-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Test Case" : "Add Test Case"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input data-testid="tc-title" className="mt-1" value={val("title") || ""} onChange={(e) => set("title", e.target.value)} /></div>
          <div>
            <Label>Module</Label>
            <Select value={val("module_id") || ""} onValueChange={(v) => set("module_id", v)}>
              <SelectTrigger className="mt-1" data-testid="tc-module"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Steps</Label><Textarea className="mt-1" value={val("steps") || ""} onChange={(e) => set("steps", e.target.value)} /></div>
          <div><Label>Expected Result</Label><Textarea className="mt-1" value={val("expected_result") || ""} onChange={(e) => set("expected_result", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="tc-submit" onClick={() => { if (!val("title")) { toast.error("Title required"); return; } onSubmit({ title: val("title"), module_id: val("module_id") || null, steps: val("steps") || "", expected_result: val("expected_result") || "" }); }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
